import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { MessageService } from 'primeng/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { BusinessUnitService } from '@app/api/user-service/api/businessUnit.service';
import { LinkStoresToBusinessUnitRequest } from '@app/api/user-service/model/linkStoresToBusinessUnitRequest';
import { BusinessUnitOptionsService } from '@shared/services/business-unit-options.service';
import { PosProfileService } from '@app/feature/center-business/pos/pos-profile/services/pos-profile.service';
import { PaginatedSelectComponent } from '@shared/components/paginated-select/paginated-select.component';
import { createStandardPaginatedLoader } from '@shared/utils/paginated-field.helpers';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-assign-store-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    PaginatedSelectComponent,
    AppButtonComponent,
  ],
  templateUrl: './assign-store-dialog.component.html',
  styleUrl: './assign-store-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignStoreDialogComponent {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly translate = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly businessUnitService = inject(BusinessUnitService);
  private readonly businessUnitOptionsService = inject(BusinessUnitOptionsService);
  private readonly posProfileService = inject(PosProfileService);

  loading = false;

  selectedBusinessUnitId: number | null = null;
  selectedStoreIds: number[] = [];

  readonly businessUnitPaginatedConfig = createStandardPaginatedLoader((params) =>
    this.businessUnitOptionsService.getDepartmentOptionsPage(params),
  );

  readonly storePaginatedConfig =
    createStandardPaginatedLoader((params) =>
      this.posProfileService.getPhysicalShopsOptionsPage(params).pipe(
        map((page) => ({
          options: page.options.map((shop) => ({
            label: shop.label,
            value: shop.value as number,
          })),
          pageContentLength: page.pageContentLength,
          totalElements: page.totalElements,
        })),
      ),
    );

  get isValid(): boolean {
    return (
      this.selectedBusinessUnitId !== null && this.selectedStoreIds.length > 0
    );
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (!this.isValid) {
      return;
    }

    this.loading = true;

    const request: LinkStoresToBusinessUnitRequest = {
      parentBusinessUnitId: this.selectedBusinessUnitId!,
      storeIds: this.selectedStoreIds,
    };

    this.businessUnitService
      .linkStoresToBusinessUnit(request, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.dialogRef.close({ success: true });
        },
        error: (error) => {
          console.error(
            '[AssignStoreDialogComponent] Error assigning stores:',
            error,
          );
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.messages.error'),
            detail: this.translate.instant(
              'configuration.business_unit.dialog.assign_store_failed',
            ),
          });
        },
      });
  }
}
